import React, { useState, useCallback, useEffect } from 'react';
import { useFontManager } from '../hooks/useFontManager';
import Canvas from '../canvas/Canvas';
import Page from './Page';
// Import shared services
import { APIService } from '../services/APIService';
import { ComponentService } from '../services/ComponentService';
import { ConfigurationService } from '../services/ConfigurationService';
import { executeComponentAction, createActionContext, validateComponentBehavior } from '../services/ActionService';
import './VehicleUI.css';

const VehicleUI = () => {
  // Core state similar to UIBuilder but focused on vehicle operation
  const [pages, setPages] = useState(new Map([['Main', new Page('Main')]]));
  const [currentPageName, setCurrentPageName] = useState('Main');
  const [canvasSize, setCanvasSize] = useState({ width: 1024, height: 768 });
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [currentConfigId, setCurrentConfigId] = useState(null);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  // Caption popup state (starts collapsed)
  const [captionText, setCaptionText] = useState("");
  const [isCaptioningEnabled, setIsCaptioningEnabled] = useState(false);

  // Auto-hide caption popup after 3 seconds
  useEffect(() => {
    if (!captionText) return;
    const timeout = setTimeout(() => {
      setCaptionText("");
    }, 3000);
    return () => clearTimeout(timeout);
  }, [captionText]);

  // Get current page
  const currentPage = pages.get(currentPageName);

  // Handle window resize for responsive scaling
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Default disability profile (hardcoded for VehicleUI performance)
  useEffect(() => {
    const defaultProfile = {
      id: 'default',
      name: 'Default',
      colors: {
        primary: '#004C97',
        secondary: '#0066CC',
        background: '#FFFFFF',
        text: '#000000',
        buttonText: '#FFFFFF'
      }
    };
    
    setSelectedProfile(defaultProfile);
    console.log('🚗 Default profile loaded:', defaultProfile.name);
    
    // Initialize canvas background for Main page
    const mainPage = pages.get('Main');
    if (mainPage) {
      mainPage.updateCanvasProperties({
        backgroundColor: defaultProfile.colors.background
      });
    }
  }, []);

  // Check for configuration in URL path and auto-load
  // URL format: http://host:port/<config-id>
  // Example: http://192.168.1.170:8080/av_control_ui/
  useEffect(() => {
    const pathname = window.location.pathname;
    // Remove leading and trailing slashes, get first segment
    const pathSegments = pathname.split('/').filter(segment => segment.length > 0);
    const configId = pathSegments[0]; // First path segment is the config ID
    
    console.log('🚗 VehicleUI checking URL path for config ID:', pathname);
    if (configId && configId !== 'mosaic' && configId !== 'editor') {
      console.log(`🚗 Auto-loading vehicle configuration from URL path: ${configId}`);
      setTimeout(() => {
        loadSpecificConfiguration(configId);
      }, 500); // Small delay to ensure profiles are loaded first
    }
  }, []);

  // Apply font and color theming
  useFontManager(selectedProfile);

  // Merge vehicle feature behavior with visual configuration using ComponentService
  const mergeVehicleFeatureWithConfig = async (configComponent) => {
    console.log('🚗 VehicleUI mergeVehicleFeatureWithConfig called with:', configComponent);
    try {
      return await ComponentService.mergeVehicleFeatureWithConfig(configComponent);
    } catch (error) {
      console.error('VehicleUI Failed to merge vehicle feature:', error);
      return configComponent;
    }
  };

  // Load a specific configuration by ID (similar to UIBuilder)
  const loadSpecificConfiguration = async (configId) => {
    console.log('🚗 Loading vehicle configuration:', configId);
    try {
      const configuration = await ConfigurationService.loadConfiguration(configId);
      if (!configuration) {
        console.warn(`❌ Configuration ${configId} not found`);
        return;
      }
      
      // Process the configuration using ConfigurationService
      const { pages: loadedPages, activePageName, canvasSize: configCanvasSize } = 
        await ConfigurationService.processConfiguration(configuration, mergeVehicleFeatureWithConfig);
      
      // Apply the loaded configuration
      setPages(loadedPages);
      setCurrentPageName(activePageName);
      setCurrentConfigId(configId);
      
      // Set canvas size if provided
      if (configCanvasSize) {
        setCanvasSize(configCanvasSize);
      }
      
      // Load and apply accessibility profile - handle both old and new formats
      let profileSettings;
      if (configuration.accessibilityProfile) {
        profileSettings = configuration.accessibilityProfile;
      } else if (configuration.accessibilitySettings) {
        // Legacy format
        profileSettings = {
          name: configuration.accessibilitySettings.profileName,
          fontFamily: configuration.accessibilitySettings.fontFamily,
          fontSize: configuration.accessibilitySettings.fontSize,
          colors: configuration.accessibilitySettings.colorScheme,
          buttonSize: configuration.accessibilitySettings.interfaceSettings?.buttonSize || 
                     configuration.accessibilitySettings.buttonSize,
          accessibility: {
            dwellTime: configuration.accessibilitySettings.dwellTime,
            requiredPressTime: configuration.accessibilitySettings.requiredPressTime
          }
        };
      }

      setIsCaptioningEnabled(!!configuration.openCaptioning);
      // Load captionText if present in config (global or per-component)
      if (configuration.captionText) {
        setCaptionText(configuration.captionText);
      } else {
        setCaptionText("");
      }

      if (profileSettings) {
        setSelectedProfile(profileSettings);
      }

      console.log(`✅ Vehicle configuration loaded: "${configuration.name}"`);
      console.log(`👤 Profile: ${profileSettings?.name || 'Unknown'}`);

      // Safely get component count
      const currentPageObj = loadedPages.get(activePageName);
      let componentCount = 0;
      if (currentPageObj && typeof currentPageObj.getComponents === 'function') {
        componentCount = currentPageObj.getComponents().length;
      } else if (Array.isArray(currentPageObj)) {
        componentCount = currentPageObj.length;
      }

      console.log(`🔧 Components: ${componentCount} on page "${activePageName}"`);
      console.log(`📄 Total pages: ${loadedPages.size}`);
      
    } catch (error) {
      console.error('❌ Failed to load vehicle configuration:', error);
    }
  };

  // Handle component updates (for toggle state changes)
  const handleComponentUpdate = useCallback((componentId, updatedComponent) => {
    console.log('🔄 VehicleUI updating component:', componentId, 'New toggle state:', updatedComponent.toggleState?.currentState);
    
    // Update the component in the current page
    const updatedPages = new Map(pages);
    const currentPageData = updatedPages.get(currentPageName);
    
    if (currentPageData && typeof currentPageData.getComponents === 'function') {
      // New Page object format
      const currentPageComponents = currentPageData.getComponents();
      const updatedPageComponents = currentPageComponents.map(comp =>
        comp.id === componentId ? { ...comp, ...updatedComponent } : comp
      );
      currentPageData.setComponents(updatedPageComponents);
    } else if (Array.isArray(currentPageData)) {
      // Legacy array format
      const updatedPageComponents = currentPageData.map(comp =>
        comp.id === componentId ? { ...comp, ...updatedComponent } : comp
      );
      updatedPages.set(currentPageName, updatedPageComponents);
    }
    
    setPages(updatedPages);
  }, [pages, currentPageName]);

  // Handle component click actions using ActionService
  const handleComponentAction = useCallback(async (componentId, event, updatedComponent = null) => {
    console.log('🚗 VehicleUI handleComponentAction called:', { componentId, hasEvent: !!event, hasUpdatedComponent: !!updatedComponent });
    
    // Use the provided updated component or get it from the page
    let component = updatedComponent;
    if (!component) {
      // Get components safely - handle both Page objects and legacy arrays
      let components = [];
      if (currentPage && typeof currentPage.getComponents === 'function') {
        components = currentPage.getComponents();
      } else if (Array.isArray(currentPage)) {
        // Legacy format - currentPage is directly an array
        components = currentPage;
      } else if (pages.has(currentPageName)) {
        const pageData = pages.get(currentPageName);
        if (pageData && typeof pageData.getComponents === 'function') {
          components = pageData.getComponents();
        } else if (Array.isArray(pageData)) {
          components = pageData;
        }
      }
      
      component = components.find(comp => comp.id === componentId);
    }
    
    if (!component || !component.behavior) {
      console.warn('❌ No component or behavior found for:', componentId);
      return;
    }

    try {
      // Validate component behavior
      const validation = validateComponentBehavior(component.behavior);
      if (!validation.valid) {
        console.warn('❌ Invalid component behavior:', validation.error);
        return;
      }

      // Create action context for VehicleUI
      const actionContext = createActionContext({
        componentId,
        currentPage: currentPageName,
        currentConfigId,
        pages,
        onPageChange: (targetPage) => {
          if (targetPage && pages.has(targetPage)) {
            setCurrentPageName(targetPage);
            console.log(`✅ Navigated to page: ${targetPage}`);
          } else {
            console.error(`❌ Page not found: ${targetPage}`);
          }
        },
        onComponentUpdate: (compId, updatedComp) => {
          // Handle component updates (especially toggle state changes) - similar to UIBuilder
          console.log('🔄 VehicleUI updating component state:', compId, 'Toggle state:', updatedComp.toggleState?.currentState);
          
          try {
            // Update the component in the current page
            const updatedPages = new Map(pages);
            const currentPageData = updatedPages.get(currentPageName);
            
            if (currentPageData && typeof currentPageData.getComponents === 'function') {
              // New Page object format
              const currentPageComponents = currentPageData.getComponents();
              const updatedPageComponents = currentPageComponents.map(comp =>
                comp.id === compId ? { ...comp, ...updatedComp } : comp
              );
              currentPageData.setComponents(updatedPageComponents);
              setPages(updatedPages);
              console.log('✅ Component state updated successfully (Page object format)');
            } else if (Array.isArray(currentPageData)) {
              // Legacy array format
              const updatedPageComponents = currentPageData.map(comp =>
                comp.id === compId ? { ...comp, ...updatedComp } : comp
              );
              updatedPages.set(currentPageName, updatedPageComponents);
              setPages(updatedPages);
              console.log('✅ Component state updated successfully (legacy array format)');
            } else {
              console.error('❌ Unable to update component - unknown page format');
            }
          } catch (updateError) {
            console.error('❌ Failed to update component state:', updateError);
          }
        },
        userId: 'vehicle_user',
        profileType: selectedProfile?.id || 'general'
      });

      // Execute action using ActionService
      const result = await executeComponentAction(
        componentId, 
        component, 
        actionContext, 
        actionContext.updateComponent
      );
      
      if (!result.success) {
        console.error('❌ Vehicle action failed:', result.error);
      } else {
        console.log('✅ Vehicle action executed:', result);
      }
    } catch (error) {
      console.error('❌ Failed to execute vehicle action:', error);
    }
  }, [currentPage, currentPageName, currentConfigId, pages, selectedProfile, handleComponentUpdate]);

  // Get current components from the current page safely
  const getCurrentComponents = () => {
    console.log('🔍 getCurrentComponents called:');
    console.log(`  Current page name: "${currentPageName}"`);
    console.log(`  Current page object:`, currentPage);
    console.log(`  Pages map size: ${pages.size}`);
    console.log(`  Pages keys:`, Array.from(pages.keys()));
    
    if (currentPage && typeof currentPage.getComponents === 'function') {
      const components = currentPage.getComponents();
      console.log(`  ✅ Found ${components.length} components via currentPage.getComponents()`);
      return components;
    } else if (currentPage && currentPage.components && Array.isArray(currentPage.components)) {
      console.log(`  ✅ Found ${currentPage.components.length} components via currentPage.components direct access`);
      return currentPage.components;
    } else if (Array.isArray(currentPage)) {
      console.log(`  ✅ Found ${currentPage.length} components via legacy array format`);
      return currentPage;
    } else if (pages.has(currentPageName)) {
      const pageData = pages.get(currentPageName);
      console.log(`  Checking page data from pages.get("${currentPageName}"):`, pageData);
      if (pageData && typeof pageData.getComponents === 'function') {
        const components = pageData.getComponents();
        console.log(`  ✅ Found ${components.length} components via pages.get().getComponents()`);
        return components;
      } else if (pageData && pageData.components && Array.isArray(pageData.components)) {
        console.log(`  ✅ Found ${pageData.components.length} components via pages.get().components direct access`);
        return pageData.components;
      } else if (Array.isArray(pageData)) {
        console.log(`  ✅ Found ${pageData.length} components via legacy pages array`);
        return pageData;
      }
    }
    console.log(`  ❌ No components found, returning empty array`);
    return [];
  };

  const currentComponents = getCurrentComponents();

  return (
    <div
      className="vehicle-ui"
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        backgroundColor: '#000000',
        overflow: 'hidden',
      }}
    >
      {/* Caption popup area (bottom, like BuilderUI) */}
      <div
        style={{
          minHeight: captionText ? 40 : 0,
          maxHeight: captionText ? 80 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s',
          background: captionText ? 'rgba(0,0,0,0.7)' : 'transparent',
          color: '#fff',
          padding: captionText ? '8px' : '0',
          fontSize: '1.1em',
          textAlign: 'center',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          borderRadius: captionText ? '8px 8px 0 0' : '0',
          pointerEvents: 'none',
        }}
        aria-live="polite"
        role="status"
      >
        {captionText}
      </div>
      {/* Canvas and fallback UI below */}
      <div style={{ flex: 1, position: 'relative' }}>
        {currentComponents.length > 0 ? (
          <div
            key={`${windowSize.width}x${windowSize.height}-${canvasSize.width}x${canvasSize.height}`}
            style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: `translateX(-50%) scale(${(() => {
                const viewportWidth = windowSize.width;
                const viewportHeight = windowSize.height;
                const padding = 20;
                const availableWidth = viewportWidth - (padding * 2);
                const availableHeight = viewportHeight - (padding * 2);
                const scaleX = availableWidth / canvasSize.width;
                const scaleY = availableHeight / canvasSize.height;
                const scale = Math.min(scaleX, scaleY);
                const minScale = 0.3;
                const maxScale = 5.0;
                const finalScale = Math.max(minScale, Math.min(maxScale, scale));
                console.log(`🔧 VehicleUI Scaling:`, {
                  viewport: `${viewportWidth}x${viewportHeight}`,
                  canvas: `${canvasSize.width}x${canvasSize.height}`,
                  available: `${availableWidth}x${availableHeight}`,
                  scaleX: scaleX.toFixed(2),
                  scaleY: scaleY.toFixed(2),
                  finalScale: finalScale.toFixed(2)
                });
                return finalScale;
              })()})`,
              transformOrigin: 'top center',
              transition: 'transform 0.3s ease',
            }}
          >
            <Canvas
              components={currentComponents}
              currentPageName={currentPageName}
              onComponentUpdate={handleComponentUpdate}
              onComponentSelect={() => {}}
              onComponentClick={handleComponentAction}
              selectedComponent={null}
              selectedProfile={selectedProfile}
              canvasProperties={currentPage?.getCanvasProperties() || {
                backgroundColor: (() => {
                  const canvasColor = currentPage?.getCanvasProperties()?.backgroundColor;
                  const profileColor = selectedProfile?.colors?.background;
                  if (canvasColor && canvasColor !== '#ffffff' && canvasColor !== 'white') {
                    return canvasColor;
                  } else if (profileColor && profileColor !== '#ffffff' && profileColor !== 'white') {
                    return profileColor;
                  } else {
                    return '#000000';
                  }
                })(),
              }}
              canvasSize={canvasSize}
              isDraggable={false}
              setCaptionText={setCaptionText}
              captionText={captionText}
              accessibilityOptions={{ openCaptioning: isCaptioningEnabled }}
            />
          </div>
        ) : (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              transformOrigin: 'top center',
              transition: 'transform 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: selectedProfile?.colors?.text || '#ffffff',
              textAlign: 'center',
            }}
            aria-live="polite"
          >
            🚗 Vehicle Interface Ready
            <small style={{ fontSize: '16px', marginTop: '10px', opacity: 0.7 }}>
              Load a configuration to see controls
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleUI;